import { useState, useCallback } from "react";
import Topbar from "../components/layout/Topbar.jsx";
import useSupervisionData from "../config/useSupervisionData.js";
import { DashboardStyles, LiveClock } from "../components/dashboard/DashboardParts.jsx";
import SupervisionTab from "../components/dashboard/SupervisionTab.jsx";
import ControleTab from "../components/dashboard/ControleTab.jsx";
import OverviewTab from "../components/dashboard/OverviewTab.jsx";
import ServersTab from "../components/dashboard/ServersTab.jsx";
import NetworkTab from "../components/dashboard/NetworkTab.jsx";
import TicketsTab from "../components/dashboard/TicketsTab.jsx";

// ─────────────────────────────────────────────
//  TABLEAU DE BORD — coque à onglets.
//  Une seule instance de useSupervisionData() alimente tous les onglets :
//  un seul cycle de rafraîchissement (30 s), pas de requêtes en double,
//  et le changement d'onglet est instantané.
// ─────────────────────────────────────────────

const TABS = [
  { id: "controle",    label: "Tour de contrôle",     icon: "bi-grid-3x3-gap" },
  { id: "supervision", label: "Supervision",          icon: "bi-activity" },
  { id: "overview",    label: "Vue d'ensemble",       icon: "bi-speedometer2" },
  { id: "serveurs",    label: "Serveurs",             icon: "bi-pc-display-horizontal" },
  { id: "reseau",      label: "Réseau & stockage",    icon: "bi-hdd-network" },
  { id: "tickets",     label: "Tickets",              icon: "bi-ticket-detailed" },
];

const STORAGE_KEY = "deskora.dashboard.tab";

function readStoredTab() {
  try {
    const v = sessionStorage.getItem(STORAGE_KEY);
    return TABS.some(t => t.id === v) ? v : "controle";
  } catch {
    return "controle";
  }
}

export default function AdminDashboardPage() {
  const data = useSupervisionData();
  const [tab, setTab] = useState(readStoredTab);
  const [netSelected, setNetSelected] = useState(null);

  const goTo = useCallback((id) => {
    setTab(id);
    try { sessionStorage.setItem(STORAGE_KEY, id); } catch { /* stockage indisponible : sans conséquence */ }
  }, []);

  // Depuis la liste d'incidents : ouvre la fiche de l'appareil dans « Réseau & stockage ».
  const openDevice = useCallback((kind, id) => {
    setNetSelected({ kind, id });
    goTo("reseau");
  }, [goTo]);

  return (
    <>
      <Topbar title="Tableau de" subtitle="bord" />
      <div className="page-body">
        <div style={{
          display: "flex", alignItems: "center", gap: 16, marginBottom: 18,
          borderBottom: "1px solid var(--border)",
          // Ne compte pas dans la largeur du parent : sans ça, la barre (7 onglets)
          // élargit toute la page sur mobile au lieu de défiler.
          contain: "inline-size",
        }}>
          <div role="tablist" aria-label="Vues du tableau de bord" style={{ display: "flex", gap: 4, overflowX: "auto", flex: "1 1 0", minWidth: 0 }}>
            {TABS.map(t => {
              const active = tab === t.id;
              return (
                <button key={t.id} role="tab" aria-selected={active} onClick={() => goTo(t.id)} style={{
                  font: "inherit", display: "flex", alignItems: "center", gap: 7, whiteSpace: "nowrap", cursor: "pointer",
                  padding: "9px 14px", fontSize: 13, fontWeight: active ? 700 : 500,
                  background: "transparent", border: "none",
                  color: active ? "var(--accent)" : "var(--text-2)",
                  borderBottom: `2px solid ${active ? "var(--accent)" : "transparent"}`,
                }}>
                  <i className={`bi ${t.icon}`} />{t.label}
                </button>
              );
            })}
          </div>
          <LiveClock compact />
        </div>

        {tab === "supervision" && <SupervisionTab data={data} onNavigate={goTo} />}
        {tab === "controle"    && <ControleTab data={data} onOpenDevice={openDevice} />}
        {tab === "overview"    && <OverviewTab data={data} onOpenDevice={openDevice} />}
        {tab === "serveurs"    && <ServersTab data={data} />}
        {tab === "reseau"      && <NetworkTab data={data} selected={netSelected} onSelect={setNetSelected} />}
        {tab === "tickets"     && <TicketsTab />}
      </div>
      <DashboardStyles />
    </>
  );
}